/**
 * Shared answer normalization for short-answer quiz grading.
 *
 * Used by both backend (quiz-grading.ts) and frontend (quiz-grading.utils.ts)
 * to ensure consistent grading behavior across preview and submission paths.
 *
 * Order of operations: trim -> collapse internal whitespace -> lowercase -> remove punctuation.
 * Each step is independently toggleable via question config. `trimWhitespace` defaults to
 * true (matches historical behavior); the others default to false.
 *
 * @requirements
 * - REQ-QUIZ-102: Question types — short-answer normalization
 * - REQ-GRADE-101: Automatic grading for objective questions
 *
 * @related KAN-194, KAN-195
 */

export interface NormalizationConfig {
  trimWhitespace?: boolean;
  ignoreInternalWhitespace?: boolean;
  caseSensitive?: boolean;
  ignorePunctuation?: boolean;
}

/**
 * Normalize an answer string for comparison using the given configuration.
 *
 * Pipeline:
 * 1. Trim leading/trailing whitespace (when `trimWhitespace !== false`)
 * 2. Collapse INTERNAL whitespace runs (when `ignoreInternalWhitespace === true`);
 *    leading/trailing whitespace is preserved per the `trimWhitespace` setting.
 *    This is critical when `trimWhitespace === false` ("Whitespace matters" rule)
 *    is combined with `ignoreInternalWhitespace === true` — see KAN-195.
 * 3. Lowercase (when `caseSensitive !== true`)
 * 4. Remove punctuation (when `ignorePunctuation === true`)
 */
export function normalizeAnswer(answer: any, config: NormalizationConfig): string {
  if (typeof answer !== 'string') {
    return String(answer ?? '');
  }

  let normalized = answer;

  if (config.trimWhitespace !== false) {
    normalized = normalized.trim();
  }

  if (config.ignoreInternalWhitespace === true) {
    // Capture leading and trailing whitespace separately so the "Whitespace
    // matters" semantics (`trimWhitespace === false`) are preserved when
    // combined with `ignoreInternalWhitespace === true`. Only INTERNAL
    // whitespace runs (between non-whitespace characters) are collapsed.
    //
    // The `s` flag lets `.` match any character including newlines so
    // multi-line answers also work. The non-greedy `.*?` ensures the final
    // `(\s*)` group captures trailing whitespace.
    //
    // Edge case: all-whitespace input. The regex greedily assigns everything
    // to `leading`, leaving `middle` and `trailing` empty. The resulting
    // concatenation `leading + '' + ''` keeps the original string intact,
    // which is the desired behavior — no duplication, no collapsing.
    const match = normalized.match(/^(\s*)(.*?)(\s*)$/s);
    if (match) {
      const [, leading, middle, trailing] = match;
      normalized = leading + middle.replace(/\s+/g, '') + trailing;
    }
  }

  if (config.caseSensitive !== true) {
    normalized = normalized.toLowerCase();
  }

  if (config.ignorePunctuation === true) {
    normalized = normalized.replace(/[^\w\s]/g, '');
  }

  return normalized;
}
